import { useAxios } from "@hooks"
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { ExamInstanceGradingSummary } from "../../../types/grading";

/**
 * Hook to recalculate all competency scores and exam pass/fail status.
 * Used to fix calculation issues after bug fixes or data corrections.
 */
export const useRecalculateExamInstance = () => {
    const axios = useAxios();
    const { t } = useTranslation();
    const { message } = App.useApp();
    const queryClient = useQueryClient();

    const recalculateExamInstance = async (examInstanceId: string): Promise<ExamInstanceGradingSummary> => {
        const response = await axios.post(`/api/grading/examinstances/${examInstanceId}/recalculate`);
        return response.data;
    }

    return useMutation({
        mutationFn: recalculateExamInstance,
        onSuccess: (_, examInstanceId) => {
            // Invalidate related queries to refresh UI
            queryClient.invalidateQueries({ queryKey: ["examInstanceTree", examInstanceId] });
            queryClient.invalidateQueries({ queryKey: ["gradingSummary", examInstanceId] });
            queryClient.invalidateQueries({ queryKey: ["pendingCorrections"] });
            message.success(t("grading.recalculateSuccess"));
        },
        onError: () => {
            message.error(t("grading.recalculateFailed"));
        }
    });
}
